import { saveUpdateContent, CONTENT_STORE_KEYS } from '../../../../../../context-controller-mod/context-serv/global-provider/local-storage-context/updateContentStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../css/contentSectionEditPageProduct.css';
import Cookies from 'js-cookie';
import ApiGet    from '../../../../../../api-controller-mod/api-serv/get-api/getApi';
import ApiPut    from '../../../../../../api-controller-mod/api-serv/put-api/putApi';
import ApiDelete from '../../../../../../api-controller-mod/api-serv/delete-api/deleteApi';
import { API_ENDPOINTS } from '../../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider';
import TextField from '../../../../../../component-controller-mod/component-serv/input-fs-cp/text-field-op/ts/textField';
import { Button, Confirmation, Toggle } from '@core/uilibrary';
import SelectMediaOverlay, {
  MediaItem as SelectMediaItem,
} from '../../../../../../component-controller-mod/component-serv/texteditor-fs-cp/select-media-overlay/ts/selectMediaOverlay';
import { useGlobalPopupProvider } from '../../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider';
import { useToast } from '../../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider';
import { DocConfirmationIcon, SuccessDocConfirmationIcon, WarningConfirmationIcon } from '../../../../../../asset-controller-mod/asset-serv/icons/iconExporter';
import EditContentBreadcrumb from '../../../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/edit-content-breadcrumb/ts/editContentBreadcrumb';

import {
  HeroSection, ProductSection, ContactSection,
  SlugResponse, ApiMediaListItem, UpdateEntry,
  ActiveSection, MediaSaveCallback,
} from './types';
import { SECTION_HUMAN, SECTION_KEY, formatBytes } from './constants';
import HeroEditor    from './heroEditor';
import ProductEditor from './productEditor';
import ContactEditor from './contactEditor';

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

const ContentSectionEditPageProduct: React.FC = () => {
  const { sectionId, contentType, contentUuid: paramContentUuid, contentVersionUuid: paramContentVersionUuid } = useParams<{ contentType: string; sectionId: string; contentUuid: string; contentVersionUuid: string }>();
  const navigate      = useNavigate();
  const { showToast } = useToast();
  const { openConfirm, closeConfirm } = useGlobalPopupProvider();

  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [contentUuid, setContentUuid] = useState(paramContentUuid ?? '');
  const [metaStatus,  setMetaStatus]  = useState('PUBLISHED');

  const [hero,    setHero]    = useState<HeroSection    | null>(null);
  const [product, setProduct] = useState<ProductSection | null>(null);
  const [contact, setContact] = useState<ContactSection | null>(null);

  const [heroSectionVisible,    setHeroSectionVisible]    = useState(true);
  const [productSectionVisible, setProductSectionVisible] = useState(true);
  const [contactSectionVisible, setContactSectionVisible] = useState(true);

  const origTitle  = useRef<string>('');
  const origAuthor = useRef<string>('');

  const origHero    = useRef<HeroSection    | null>(null);
  const origProduct = useRef<ProductSection | null>(null);
  const origContact = useRef<ContactSection | null>(null);

  const isSavedRef = useRef(false);
  const origStatus = useRef<string>('PUBLISHED');

  const userId             = Cookies.get('userUuid')           ?? '';
  const username           = Cookies.get('username')            ?? '';
  const contentVersionUuid = paramContentVersionUuid ?? Cookies.get('contentVersionUuid') ?? '';

  const [mediaOverlayOpen,         setMediaOverlayOpen]         = useState(false);
  const [mediaOverlayItems,        setMediaOverlayItems]        = useState<SelectMediaItem[]>([]);
  const [mediaOverlayOnSave,       setMediaOverlayOnSave]       = useState<(() => MediaSaveCallback) | null>(null);
  const [mediaOverlayHasMoreState, setMediaOverlayHasMoreState] = useState(true);
  const [mediaOverlayLoadingMore,  setMediaOverlayLoadingMore]  = useState(false);

  const mediaUuidMap        = useRef<Map<number, string>>(new Map());
  const mediaOverlayPageRef = useRef(1);
  const mediaOverlayHasMore = useRef(true);
  const mediaOverlayLoading = useRef(false);
  const OVERLAY_PAGE_SIZE   = 12;

  const activeSectionKey: ActiveSection = (() => {
    if (sectionId === 'productSection') return 'productSection';
    if (sectionId === 'contactSection') return 'contactSection';
    return 'heroSection';
  })();

  const sectionLabel = SECTION_HUMAN[activeSectionKey];

  // ── Fetch by slug ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    ApiGet<SlugResponse>(
      `${API_ENDPOINTS.GET_CMS_CONTENT}?slug=product`
    )
      .then(res => {
        if (cancelled) return;
        const resData = res?.data?.data;
        const cta     = resData?.data?.product?.cta?.[0];
        if (!cta) return;

        // FIX 1: use contentUuid, never contentId
        setContentUuid(prev => prev || String(resData.contentId ?? ''));

        setHero(cta.heroSection);
        setProduct(cta.productSection);
        setContact(cta.contactSection);
        setHeroSectionVisible(cta.heroSection?.visible ?? true);
        setProductSectionVisible(cta.productSection?.visible ?? true);
        setContactSectionVisible(cta.contactSection?.visible ?? true);

        origHero.current    = cta.heroSection;
        origProduct.current = cta.productSection;
        origContact.current = cta.contactSection;

        const active =
          activeSectionKey === 'productSection' ? cta.productSection :
          activeSectionKey === 'contactSection' ? cta.contactSection :
          cta.heroSection;

        origTitle.current  = active?.title  ?? '';
        origAuthor.current = username;
        setMetaStatus((active as any)?.status ?? 'PUBLISHED');
        origStatus.current = (active as any)?.status ?? 'PUBLISHED';
      })
      .catch(err => { if (!cancelled) console.error('[ProductSectionEdit] fetch:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [activeSectionKey]);

  // ── Delete draft ───────────────────────────────────────────────────────────
  const deleteDraft = useCallback(async () => {
    if (!contentVersionUuid) return;

    const raw      = localStorage.getItem('isDelete');
    const isDelete = raw !== null ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : null;

    if (isDelete === false) {
      localStorage.removeItem('isDelete');
      return;
    }

    localStorage.removeItem('isDelete');
    try {
      await ApiDelete(
        `${API_ENDPOINTS.DELETE_DRAFT_CONTENT}?version=${contentVersionUuid}`
      );
    } catch (err) {
      console.warn('[ProductSectionEdit] delete draft failed:', err);
    } finally {
      Cookies.remove('contentVersionUuid');
    }
  }, [contentVersionUuid]);

  // ── On unmount ─────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (!isSavedRef.current) {
        deleteDraft();
      } else {
        localStorage.removeItem('isDelete');
      }
    };
  }, [deleteDraft]);

const buildUpdates = useCallback((nextStatus: string): UpdateEntry[] => {
  const updates:      UpdateEntry[] = [];
  const fieldUpdates: UpdateEntry[] = [];

  // ── Hero ──────────────────────────────────────────────────────────────────
  if (activeSectionKey === 'heroSection' && hero && origHero.current) {
    const orig = origHero.current;
    const base = ['product', 'cta', 0, 'heroSection'] as const;

    // section visible — always send
    fieldUpdates.push({ path: [...base, 'visible'],                   value: heroSectionVisible });
    // primaryButton.visible — always send
    fieldUpdates.push({ path: [...base, 'primaryButton', 'visible'], value: hero.primaryButton.visible });

    if (hero.title !== orig.title)
      fieldUpdates.push({ path: [...base, 'title'],                   value: hero.title });
    if (hero.primaryButton.label !== orig.primaryButton.label)
      fieldUpdates.push({ path: [...base, 'primaryButton', 'label'], value: hero.primaryButton.label });

    updates.push(...fieldUpdates);
    updates.push({ path: [...base, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
    fieldUpdates.length = 0;
  }

  // ── Product ───────────────────────────────────────────────────────────────
  if (activeSectionKey === 'productSection' && product && origProduct.current) {
    const orig = origProduct.current;
    const base = ['product', 'cta', 0, 'productSection'] as const;

    // section visible — always send
    fieldUpdates.push({ path: [...base, 'visible'], value: productSectionVisible });

    if (product.label !== orig.label)
      fieldUpdates.push({ path: [...base, 'label'],       value: product.label });
    if (product.title !== orig.title)
      fieldUpdates.push({ path: [...base, 'title'],       value: product.title });
    if (product.description !== orig.description)
      fieldUpdates.push({ path: [...base, 'description'], value: product.description });

    // buildUpdates → product section
    const listsChanged = product.lists.some((item, idx) => {
      const o = orig.lists[idx];
      if (!o) return true;
      return (
        item.title       !== o.title       ||
        item.description !== o.description ||
        item.img         !== o.img         ||
        item.items.some((ic, j) =>
          ic.label !== o.items[j]?.label ||
          ic.Icon  !== o.items[j]?.Icon   // ← add this
        )
      );
    });
    if (listsChanged)
      fieldUpdates.push({ path: [...base, 'lists'], value: product.lists });

    updates.push(...fieldUpdates);
    updates.push({ path: [...base, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
    fieldUpdates.length = 0;
  }

  // ── Contact ───────────────────────────────────────────────────────────────
  if (activeSectionKey === 'contactSection' && contact && origContact.current) {
    const orig = origContact.current;
    const base = ['product', 'cta', 0, 'contactSection'] as const;

    // section visible — always send
    fieldUpdates.push({ path: [...base, 'visible'], value: contactSectionVisible });

    if (contact.label !== orig.label)
      fieldUpdates.push({ path: [...base, 'label'],       value: contact.label });
    if (contact.title !== orig.title)
      fieldUpdates.push({ path: [...base, 'title'],       value: contact.title });
    if (contact.description !== orig.description)
      fieldUpdates.push({ path: [...base, 'description'], value: contact.description });

    const listsChanged = contact.lists.some((item, idx) => {
      const o = orig.lists[idx];
      if (!o) return true;
      return (
        item.title       !== o.title       ||
        item.description !== o.description ||
        item.buttonLabel !== o.buttonLabel ||
        item.imgIco      !== o.imgIco
      );
    });
    if (listsChanged)
      fieldUpdates.push({ path: [...base, 'lists'], value: contact.lists });

    updates.push(...fieldUpdates);
    updates.push({ path: [...base, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
  }

  return updates;
}, [
  activeSectionKey,
  hero, product, contact,
  heroSectionVisible, productSectionVisible, contactSectionVisible,
]);
  // ── Register media usage ───────────────────────────────────────────────────
  const registerMediaUsage = useCallback((mediaUuid: string) => {
    if (!mediaUuid || !contentUuid) return;
    ApiGet(
      `/api/v1/media/usage?mediaUuid=${mediaUuid}&entityType=CONTENT&entityUuid=${contentUuid}`
    ).catch(err => console.warn('[MediaUsage] register failed:', err));
  }, [contentUuid]);

  // ── Save ───────────────────────────────────────────────────────────────────
  const performSave = useCallback(async () => {
    setSaving(true);
    try {
      await ApiPut(
        `${API_ENDPOINTS.UPDATE_CONTENT_BY_PATH}?contentUuid=${contentUuid}&userId=${userId}&versionUuid=${contentVersionUuid}`,
        { type: 'page', title: `Product Page - ${sectionLabel}`, version: 1, updates: buildUpdates(metaStatus) },
      );
      isSavedRef.current = true;
      // FIX 3: always use CONTENT_STORE_KEYS.PRODUCT + activeSectionKey (never sectionId fallback)
      saveUpdateContent(
        CONTENT_STORE_KEYS.PRODUCT,
        contentType ?? 'product', contentUuid, contentVersionUuid, activeSectionKey,
      );
      localStorage.removeItem('isDelete');
      Cookies.remove('contentVersionUuid');
      showToast({ status: 'success', title: 'Complete !',   description: 'Section was updated successfully.' });
      navigate(`/workflow/manage/preview/${contentUuid}/${contentVersionUuid}/draft`);
    } catch (err) {
      console.error('[ProductSectionEdit] save:', err);
      showToast({ status: 'error',   title: 'Uncomplete !', description: 'Section was updated unsuccessfully.' });
    } finally {
      setSaving(false);
    }
  }, [buildUpdates, metaStatus, activeSectionKey, contentUuid, userId, contentVersionUuid, sectionLabel, contentType, navigate, showToast]);

  // ── Fetch one page of media for the overlay ────────────────────────────────
  const fetchMediaPage = useCallback(async (page: number, isLoadMore = false) => {
    if (mediaOverlayLoading.current) return;
    mediaOverlayLoading.current = true;
    if (isLoadMore) setMediaOverlayLoadingMore(true);
    try {
      const res     = await ApiGet<{ success: boolean; data: { items: ApiMediaListItem[]; pagination: { hasNext: boolean } } }>(
        `${API_ENDPOINTS.GET_ALL_MEDIA}?page=${page}&size=${OVERLAY_PAGE_SIZE}`
      );
      const raw     = res?.data?.data?.items             ?? [];
      const hasNext = res?.data?.data?.pagination?.hasNext ?? false;

      const mapped: SelectMediaItem[] = raw.map((m: ApiMediaListItem) => ({
        id:       m.id,
        src:      m.url,
        alt:      m.alt  ?? '',
        title:    m.name ?? '',
        size:     m.size ? formatBytes(m.size) : '',
        isActive: m.status === 'ACTIVE' || m.status === 'PUBLISHED',
      }));

      raw.forEach((m: ApiMediaListItem) => {
        mediaUuidMap.current.set(m.id, m.uuid ?? '');
      });

      mediaOverlayHasMore.current = hasNext;
      setMediaOverlayHasMoreState(hasNext);
      setMediaOverlayItems(prev => isLoadMore ? [...prev, ...mapped] : mapped);
    } catch {
      showToast({ status: 'error', title: 'Error', description: 'Failed to load media.' });
      if (!isLoadMore) setMediaOverlayItems([]);
    } finally {
      mediaOverlayLoading.current = false;
      if (isLoadMore) setMediaOverlayLoadingMore(false);
    }
  }, [showToast]);

  // ── Open media overlay ─────────────────────────────────────────────────────
  const openMediaOverlay = useCallback((onSave: MediaSaveCallback) => {
    mediaOverlayPageRef.current = 1;
    mediaOverlayHasMore.current = true;
    mediaOverlayLoading.current = false;
    setMediaOverlayItems([]);
    setMediaOverlayHasMoreState(true);
    setMediaOverlayLoadingMore(false);
    setMediaOverlayOnSave(() => () => (item: SelectMediaItem | null) => {
      onSave(item);
      if (item) {
        const uuid = mediaUuidMap.current.get(item.id);
        if (uuid) registerMediaUsage(uuid);
      }
    });
    setMediaOverlayOpen(true);
    fetchMediaPage(1, false);
  }, [fetchMediaPage, registerMediaUsage]);

  // ── Load next media page ───────────────────────────────────────────────────
  const handleMediaLoadMore = useCallback(() => {
    if (!mediaOverlayHasMore.current || mediaOverlayLoading.current) return;
    const nextPage = mediaOverlayPageRef.current + 1;
    mediaOverlayPageRef.current = nextPage;
    fetchMediaPage(nextPage, true);
  }, [fetchMediaPage]);

  // ── Confirm save ───────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    openConfirm(
      <Confirmation visible title="Save changes"
        description={`Are you sure you want to save changes to the "${sectionLabel}" section? This will update the live content.`}
        status="info" icon={<SuccessDocConfirmationIcon />} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost"   size="md" value="Cancel" type="button" onClick={closeConfirm} />
          <Button variant="primary" size="md" value="Save"   type="button"
            onClick={() => { closeConfirm(); performSave(); }} />
        </>}
      />
    );
  }, [openConfirm, closeConfirm, sectionLabel, performSave]);

  // ── Confirm cancel / discard ───────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    openConfirm(
      <Confirmation visible title="Discard changes"
        description="Are you sure you want to discard all unsaved changes? This action cannot be undone."
        status="warning" icon={<DocConfirmationIcon />} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost"  size="md" value="Cancel"    type="button" onClick={closeConfirm} />
          <Button variant="danger" size="md" value="Discard" type="button"
            onClick={async () => {
              closeConfirm();
              await deleteDraft();
              isSavedRef.current = true;
              localStorage.removeItem('isDelete');
              navigate(`/content/${contentType ?? 'product'}`);
            }} />
        </>}
      />
    );
  }, [openConfirm, closeConfirm, navigate, contentType, deleteDraft]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="scl--cms-content-edit-product-root">
        <div className="scl--cms-content-edit-product-skeleton-wrap">
          <div className="scl--cms-content-edit-product-skeleton scl--cms-content-edit-product-skeleton--title" />
          <div className="scl--cms-content-edit-product-skeleton scl--cms-content-edit-product-skeleton--desc"  />
          <div className="scl--cms-content-edit-product-skeleton scl--cms-content-edit-product-skeleton--field" />
          <div className="scl--cms-content-edit-product-skeleton scl--cms-content-edit-product-skeleton--field" />
          <div className="scl--cms-content-edit-product-skeleton scl--cms-content-edit-product-skeleton--field" />
        </div>
      </div>
    );
  }

  if (!hero && !product && !contact) {
    return (
      <div className="scl--cms-content-edit-product-root">
        <div className="scl--cms-content-edit-product-empty-state">
          <p className="scl--cms-content-edit-product-empty-title">Section not found</p>
          <p className="scl--cms-content-edit-product-empty-desc">Could not load "{activeSectionKey}" for product.</p>
          <Button variant="ghost" value="← Back" onClick={() => { localStorage.removeItem('isDelete'); navigate(`/content/${contentType ?? 'product'}`); }} />
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="scl--cms-content-edit-product-root">
      <EditContentBreadcrumb
        onHomeClick={() => { localStorage.removeItem('isDelete'); navigate('/overview'); }}
        onContentClick={() => { localStorage.removeItem('isDelete'); navigate(`/content/${contentType ?? 'product'}`); }}
      />
      <h1 className="scl--cms-content-edit-product-page-title">Edit section</h1>
      <p className="scl--cms-content-edit-product-page-desc">
        Responsible for managing and maintaining website content to ensure accuracy.
      </p>

      <div className="scl--cms-content-edit-product-meta-grid">
        <TextField label="Section ID" value={SECTION_KEY[activeSectionKey]} disabled />
        <TextField label="Author"     value={origAuthor.current} disabled />
        <TextField label="Section"    value={sectionLabel}       disabled />
        <TextField label="Title"      value={origTitle.current}  disabled />
      </div>

      <div className="scl--cms-content-edit-product-section-editor">
        <div className="scl--cms-content-edit-product-section-heading-row">
          <h2 className="scl--cms-content-edit-product-section-heading">
            Product-{sectionLabel} sector
          </h2>
          {activeSectionKey === 'heroSection'    && <Toggle checked={heroSectionVisible}    onChange={setHeroSectionVisible}    />}
          {activeSectionKey === 'productSection' && <Toggle checked={productSectionVisible} onChange={setProductSectionVisible} />}
          {activeSectionKey === 'contactSection' && <Toggle checked={contactSectionVisible} onChange={setContactSectionVisible} />}
        </div>
        <div className="scl--cms-content-edit-product-divider" />

        {activeSectionKey === 'heroSection' && hero && (
          <HeroEditor data={hero} onChange={setHero} />
        )}
        {activeSectionKey === 'productSection' && product && (
          <ProductEditor
            data={product}
            onChange={setProduct}
            onMediaClick={openMediaOverlay}
            userId={userId}
            registerMediaUsage={registerMediaUsage}
          />
        )}
        {activeSectionKey === 'contactSection' && contact && (
          <ContactEditor
            data={contact}
            onChange={setContact}
            onMediaClick={openMediaOverlay}
            userId={userId}
            registerMediaUsage={registerMediaUsage}
          />
        )}
      </div>

      <div className="scl--cms-content-edit-product-actions">
        <Button variant="ghost"   value="Cancel"                      onClick={handleCancel} />
        <Button variant="primary" value={saving ? 'Saving…' : 'Save'} onClick={handleSave}   disabled={saving} />
      </div>

      {mediaOverlayOpen && (
        <SelectMediaOverlay
          items={mediaOverlayItems}
          hasMore={mediaOverlayHasMoreState}
          loadingMore={mediaOverlayLoadingMore}
          onLoadMore={handleMediaLoadMore}
          onSave={item => {
            if (mediaOverlayOnSave) { const cb = mediaOverlayOnSave(); cb(item); }
            setMediaOverlayOpen(false);
            setMediaOverlayOnSave(null);
          }}
          onClose={() => {
            setMediaOverlayOpen(false);
            setMediaOverlayOnSave(null);
          }}
        />
      )}
    </div>
  );
};

export default ContentSectionEditPageProduct;